/**
 * useOrdersV2 Hook
 * 訂單管理 v2 React Query Hook — Phase 2
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { OPERATIONS_SUMMARY_KEY } from "@/hooks/useOperationsSummary";
import type { SignatureReceipt, SignatureSubmission } from "@shared/orderSignature";
import { isSignatureReceipt } from "@/lib/signatureJournal";
import type {
  OrderV2,
  OrderGroupV2,
  OrdersV2ListResponse,
  OrdersV2GroupListResponse,
  CreateOrderV2Request,
  UpdateOrderStatusRequest,
  DualGroupedOrders,
  OrderStatusV2,
} from "@/lib/types";

const ORDERS_KEY = "/api/orders";
const ORDER_GROUPS_KEY = "/api/orders/groups";
// G-12: 訂單完成/封存後會進入歷史與月報，但這兩張表的 query key 前綴與
// /api/orders 不同，原本的 invalidate 打不到，需另外失效（對齊
// useHistoryOrders.ts / useReports.ts 的 key 命名）。
const HISTORY_KEY = "/api/history";
const MONTHLY_REPORT_KEY = "/api/reports/monthly";

export interface ShipmentShortageEntry {
  rawMaterialId: number;
  rawMaterialCode: string;
  rawMaterialName: string;
  shortageQuantity: number;
  currentBalance: number;
  deductedQuantity: number;
}

export type OrderStatusUpdateResult = OrderV2 & {
  shipmentShortages?: ShipmentShortageEntry[];
};

/**
 * G-12：任何改動訂單狀態/數量的 mutation，都必須連同「營運指標聚合」快取一起失效。
 * 首頁三格漏斗與今日工作台的指標改取自 GET /api/reports/operations-summary
 * （useOperationsSummary），若只失效清單而不失效此 key，指標會在同頁互動後停留舊值，
 * 直到整頁重新整理才更新（queryClient 全域 refetchOnWindowFocus/refetchInterval 皆關閉）。
 */
function invalidateOrderQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: [ORDERS_KEY], exact: false });
  queryClient.invalidateQueries({ queryKey: [ORDER_GROUPS_KEY], exact: false });
  queryClient.invalidateQueries({ queryKey: [OPERATIONS_SUMMARY_KEY] });
}

/** 出貨扣料會改變原料列表、低庫存、單筆餘額及交易紀錄。 */
function invalidateRawMaterialQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["raw-materials"], exact: false });
  queryClient.invalidateQueries({ queryKey: ["raw-materials-low-stock"], exact: false });
  queryClient.invalidateQueries({ queryKey: ["raw-material-inventory"], exact: false });
  queryClient.invalidateQueries({ queryKey: ["raw-material-transactions"], exact: false });
}

type OrderQueryOptions = {
  status?: OrderStatusV2;
  customerId?: number;
  dateFrom?: string;
  dateTo?: string;
  enabled?: boolean;
};

function buildOrdersQueryString(options: Omit<OrderQueryOptions, "enabled">) {
  const params = new URLSearchParams();

  if (options.status) params.set("status", options.status);
  if (options.customerId) params.set("customerId", String(options.customerId));
  if (options.dateFrom) params.set("dateFrom", options.dateFrom);
  if (options.dateTo) params.set("dateTo", options.dateTo);

  return params.toString();
}

/**
 * Helper: group orders by date → customer
 */
export function groupOrdersByDateCustomer(orders: OrderV2[]): DualGroupedOrders {
  const result: DualGroupedOrders = {};

  for (const order of orders) {
    const dateKey = (order.orderDate || "").split("T")[0] || "未指定日期";
    const customerKey = order.customerName || "未指定客戶";

    if (!result[dateKey]) result[dateKey] = {};
    if (!result[dateKey][customerKey]) result[dateKey][customerKey] = [];
    result[dateKey][customerKey].push(order);
  }

  return result;
}

/**
 * Helper: group orders by date
 */
export function groupOrdersByDate(orders: OrderV2[]): Record<string, OrderV2[]> {
  const result: Record<string, OrderV2[]> = {};

  for (const order of orders) {
    const dateKey = (order.orderDate || "").split("T")[0] || "未指定日期";

    if (!result[dateKey]) {
      result[dateKey] = [];
    }

    result[dateKey].push(order);
  }

  return result;
}

/**
 * 取得訂單列表 (v2)
 */
export function useOrdersV2(options: {
  status?: OrderStatusV2;
  customerId?: number;
  dateFrom?: string;
  dateTo?: string;
  enabled?: boolean;
} = {}) {
  const { status, customerId, dateFrom, dateTo, enabled = true } = options;
  const queryString = buildOrdersQueryString({ status, customerId, dateFrom, dateTo });

  return useQuery<OrderV2[]>({
    queryKey: [ORDERS_KEY, queryString],
    enabled,
    queryFn: async ({ queryKey }) => {
      const [, currentQueryString] = queryKey as [string, string];
      const requestUrl = currentQueryString ? `${ORDERS_KEY}?${currentQueryString}` : ORDERS_KEY;
      const res = await apiRequest("GET", requestUrl);
      const data = (await res.json()) as OrdersV2ListResponse;

      return data.orders ?? [];
    },
  });
}

/**
 * ??閮??蝞∠? (v2)
 */
export function useOrderGroupsV2(options: OrderQueryOptions = {}) {
  const { status, customerId, dateFrom, dateTo, enabled = true } = options;
  const queryString = buildOrdersQueryString({ status, customerId, dateFrom, dateTo });

  return useQuery<OrderGroupV2[]>({
    queryKey: [ORDER_GROUPS_KEY, queryString],
    enabled,
    queryFn: async ({ queryKey }) => {
      const [, currentQueryString] = queryKey as [string, string];
      const requestUrl = currentQueryString ? `${ORDER_GROUPS_KEY}?${currentQueryString}` : ORDER_GROUPS_KEY;
      const res = await apiRequest("GET", requestUrl);
      const data = (await res.json()) as OrdersV2GroupListResponse;

      return data.groups ?? [];
    },
  });
}

/**
 * 取得單一訂單 (v2)
 */
export function useOrderById(id: string | null) {
  return useQuery<OrderV2>({
    queryKey: [`${ORDERS_KEY}/${id}`],
    enabled: !!id,
  });
}

/**
 * 建立訂單 (v2)
 */
export function useCreateOrderV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateOrderV2Request) => {
      const res = await apiRequest("POST", ORDERS_KEY, data);
      return (await res.json()) as OrderV2;
    },
    onSuccess: () => {
      invalidateOrderQueries(queryClient);
    },
  });
}

/**
 * 更新訂單 (v2)
 */
export function useUpdateOrderV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateOrderV2Request>;
    }) => {
      const res = await apiRequest("PATCH", `${ORDERS_KEY}/${id}`, data);
      return (await res.json()) as OrderV2;
    },
    onSuccess: (_data, variables) => {
      invalidateOrderQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: [`${ORDERS_KEY}/${variables.id}`] });
    },
  });
}

/**
 * 更新訂單狀態 (v2)
 */
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateOrderStatusRequest;
    }) => {
      const res = await apiRequest("PATCH", `${ORDERS_KEY}/${id}/status`, data);
      return (await res.json()) as OrderStatusUpdateResult;
    },
    onSuccess: (_data, variables) => {
      invalidateOrderQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: [`${ORDERS_KEY}/${variables.id}`] });
      if (variables.data.status === "shipping") {
        invalidateRawMaterialQueries(queryClient);
      }
      // G-12: 轉為完成/封存的訂單才會出現在歷史與月報，僅此時失效以免無謂 refetch
      if (variables.data?.status === "delivered" || variables.data?.status === "archived") {
        queryClient.invalidateQueries({ queryKey: [HISTORY_KEY], exact: false });
        queryClient.invalidateQueries({ queryKey: [MONTHLY_REPORT_KEY], exact: false });
      }
    },
  });
}

/**
 * 刪除訂單 (v2)
 */
export function useDeleteOrderV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `${ORDERS_KEY}/${id}`);
    },
    onSuccess: (_data, id) => {
      invalidateOrderQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: [`${ORDERS_KEY}/${id}`] });
    },
  });
}

/**
 * 儲存簽名並自動轉 delivered (自行配送)
 */
export function useSaveSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: async ({
      id,
      actorId,
      data,
      signal,
    }: {
      id: string;
      actorId: number;
      data: SignatureSubmission;
      signal?: AbortSignal;
    }) => {
      const res = await apiRequest("POST", `${ORDERS_KEY}/${id}/signature`, data, { signal, expectedActorId: actorId });
      const receipt: unknown = await res.json();
      if (!isSignatureReceipt(receipt, id, data.requestKey, data.expectedContentVersion)) {
        throw new Error("簽收回執不完整，請查核送出結果。");
      }
      return receipt as SignatureReceipt;
    },
    onSuccess: (_data, variables) => {
      invalidateOrderQueries(queryClient);
      // 簽收會把 shipping 訂單轉為 delivered；driver 隨即失去該明細的
      // 讀取權限。明細仍需標記 stale，但不要在簽收頁尚未卸載時立即重抓，
      // 否則合法的成功流程會產生 403 console error。
      queryClient.invalidateQueries({
        queryKey: [`${ORDERS_KEY}/${variables.id}`],
        refetchType: "none",
      });
      // G-12: 簽收完成後訂單進入已送達，歷史與月報快取需失效
      queryClient.invalidateQueries({ queryKey: [HISTORY_KEY], exact: false });
      queryClient.invalidateQueries({ queryKey: [MONTHLY_REPORT_KEY], exact: false });
    },
  });
}
